import { useEffect, useState } from "react";
import { Button, ChangePassword, Icon, Login, PageHeader, t } from "@mypos/ui";
import { api } from "./lib/api";
import { ManagerShell } from "./shell/ManagerShell";
import { ManagerPagesRouter } from "./shell/PagesRouter";
import { OfflineBanner } from "./shell/OfflineBanner";
import { LicenseBanner } from "./shell/LicenseBanner";
import { useAuthStore } from "./lib/authStore";
import { useLocaleStore } from "./lib/localeStore";

export function App() {
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);
  const error = useAuthStore((s) => s.error);
  const loginEmail = useAuthStore((s) => s.loginEmail);
  const logout = useAuthStore((s) => s.logout);
  const hydrate = useAuthStore((s) => s.hydrate);
  const hydrateLocale = useLocaleStore((s) => s.hydrate);
  // Subscribing forces App to re-render when the language toggles.
  useLocaleStore((s) => s.locale);

  useEffect(() => {
    void hydrate();
    void hydrateLocale();
  }, [hydrate, hydrateLocale]);

  if (loading) {
    return (
      <>
        <OfflineBanner />
        <div style={{ padding: 40, fontFamily: "Phetsarath OT, Inter, system-ui" }}>Loading…</div>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <OfflineBanner />
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--bg, #FAF7F2)",
          }}
        >
          <Login
            onEmailLogin={({ email, password }) => loginEmail(email, password)}
            error={error}
            busy={loading}
          />
        </div>
      </>
    );
  }

  // Cashier role sees a bounce page per PWA_PLAN §12 — cashiers belong on the
  // Till app, not the Manager PWA.
  if (user.role === "cashier") {
    return (
      <>
        <OfflineBanner />
        <div style={{ padding: 40 }}>
          <PageHeader
            title="This is MyPOS"
            subtitle="Cashiers should use the Till app for day-to-day selling. Ask your manager if you need access here."
            actions={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void logout()}
                icon={<Icon name="log-out" size="sm" />}
                aria-label={t("common.signOut")}
              >
                {t("common.signOut")}
              </Button>
            }
          />
        </div>
      </>
    );
  }

  // Forced password change gate — blocks the shell until the flag clears.
  if (user.mustChangePassword) {
    return (
      <>
        <OfflineBanner />
        <ChangePasswordGate onDone={() => void hydrate()} />
      </>
    );
  }

  return (
    <>
      <OfflineBanner />
      <LicenseBanner />
      <ManagerShell>
        <ManagerPagesRouter />
      </ManagerShell>
    </>
  );
}

/**
 * Wires the shared @mypos/ui ChangePassword page to the Manager's API + auth
 * store. Mirrors how the Till's ChangePasswordConnected wraps the same page;
 * on success it re-hydrates the Manager auth store so mustChangePassword clears
 * and App falls through to ManagerShell.
 */
function ChangePasswordGate({ onDone }: { onDone: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(currentPassword: string, newPassword: string) {
    setError(null);
    setBusy(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return <ChangePassword onSubmit={onSubmit} error={error} busy={busy} />;
}
